/* ═══════════════════════════════════════════════════════════════
   ModuleBrowser — ONE shared browser for the firm's module library,
   used by BOTH the Module Library page and the Spec Studio / Designer
   "Modules" panel, so browsing looks and behaves identically everywhere
   (migration 056 — `module_categories`, `module_library.tags`).

   The category tree's fetch + live sync, the breadcrumb/drill-down
   navigation and the New/Rename/Move/Delete dialogs all live in
   `useModuleCategoryBrowser` (delete reparents, never deletes a module);
   this component renders the standard DataTable of modules in scope with
   Category/Tags columns layered onto whatever columns the host supplies,
   plus the category cards (`CategoryEntryCard`) and dialogs
   (`ModuleCategoryDialogs`) that hook's state drives.

   Host-chosen LAYOUT, never a width media query:
     rail    — categories in a left column (library page).
     stacked — breadcrumb + categories above the table (the panel); the
               host also says whether it is docked narrow (`compact`),
               which trims the table to name + tags by default.

   What the host still owns: loading the module list itself (archive
   filter, etc.), selection/bulk actions, row click, and any columns of
   its own (Chunks, Blocks, Version…). This component never touches
   `module_library` directly except through `ModuleService.updateMeta`
   callers wire in via `onEditModuleMeta` — it only reads the `modules`
   prop.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Folder, ChevronRight, Plus } from 'lucide-react';
import { DataTable, type BulkActions, type Column, type GroupOption } from './DataTable';
import { Button } from './ui/button';
import EmptyState from './EmptyState';
import { Icon } from './ui/Icon';
import { rem } from '../lib/rem';
import { Prefs } from '../services/prefsService';
import { CategoryEntryCard } from './ModuleCategoryEntryCard';
import { ModuleCategoryDialogs } from './ModuleCategoryDialogs';
import { ModuleTagsRail } from './ModuleTagsRail';
import { useModuleCategoryBrowser } from './useModuleCategoryBrowser';
import type { ModuleCategory } from '../../shared/module-categories';
import type { SpecModule } from '../../shared/spec-modules';
import {
  categoryColumnValue,
  categoryFilterOptions,
  MODULE_BROWSER_STACKED_VISIBLE,
  moduleBrowserRailWidthPx,
  tagsColumnValue,
} from '../lib/moduleBrowserColumns';

// ─── v1 → v2 storage-key rename: one-time seed (modules-polish fix-up) ───
// DataTable's per-table prefs all live under `dt_<suffix>_<storageKey>`, in
// BOTH localStorage and the per-user cloud mirror (`Prefs`, same key
// string). Bumping `storageKeyBase` therefore orphans every one of a
// user's saved columns/order/widths/sort/group/filters/text-size for the
// table unless something seeds the new key from the old one first.
const DT_PREF_SUFFIXES = [
  'sortKey',
  'sortAsc',
  'groupKey',
  'collapsed',
  'filters',
  'textScale',
  'order',
  'widths',
  'orderAt',
  'hidden',
  'hiddenAt',
] as const;

/** Copies each v1 pref to v2 — only where v2 doesn't already have one, so
 *  this is naturally idempotent: once seeded, v2 is no longer absent and
 *  every later call (every re-render) is a no-op. `skipHidden` drops the
 *  hidden-columns list (and its timestamp) — the ONE stale value that was
 *  the actual reason for the bump; everything else about a saved layout
 *  carries over untouched. Must run synchronously during render (never an
 *  effect) so it lands before DataTable's own lazy `useState`
 *  initializers read these same keys on its first mount. */
function seedV2FromV1(v1Key: string, v2Key: string, skipHidden: boolean): void {
  for (const suffix of DT_PREF_SUFFIXES) {
    if (skipHidden && (suffix === 'hidden' || suffix === 'hiddenAt')) continue;
    const v1Full = `dt_${suffix}_${v1Key}`;
    const v2Full = `dt_${suffix}_${v2Key}`;
    try {
      if (localStorage.getItem(v2Full) == null) {
        const prior = localStorage.getItem(v1Full);
        if (prior != null) localStorage.setItem(v2Full, prior);
      }
    } catch {
      /* private mode / storage unavailable — cloud seed below still runs */
    }
    // Cloud mirror — `Prefs.get`/`.set` are a synchronous in-memory map
    // once loaded (no extra server round trip); the existing debounced
    // write-through is what actually persists it.
    if (Prefs.isReady() && Prefs.get(v2Full) == null) {
      const priorCloud = Prefs.get(v1Full);
      if (priorCloud != null) Prefs.set(v2Full, priorCloud);
    }
  }
}

export interface ModuleBrowserProps {
  /** Scopes the "last open category" preference and the DataTable's
   *  saved-view storage key — unique per surface (`library`, `panel`). */
  hostId: string;
  layout: 'rail' | 'stacked';
  /** `stacked` only: the host measured its own dock container and found
   *  it narrow — trims the table to name + tags by default. */
  compact?: boolean;
  /** Already loaded and filtered by the host (e.g. the archive toggle). */
  modules: SpecModule[];
  userName: string;
  userPermissions: string[];
  toast: (msg: string, isError?: boolean) => void;
  storageKeyBase: string;
  /** The host's own columns — must include one with `key: 'name'`. */
  columns: Column<SpecModule>[];
  groupOptions?: GroupOption<SpecModule>[];
  onRowClick?: (m: SpecModule) => void;
  /** Address a row opens (Module Library only); pickers omit it. */
  rowHref?: (m: SpecModule) => string | undefined;
  selectable?: boolean;
  /** Passed to the DataTable — `'checkbox'` = plain click toggles the box. */
  selectionMode?: 'explorer' | 'checkbox';
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>, items: SpecModule[]) => void;
  bulkActions?: BulkActions<SpecModule>;
  searchPlaceholder?: string;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /** Category/Tags cell click → "Category and tags…" (library's row
   *  action). Omit on a host with no per-row metadata edit (the panel). */
  onEditModuleMeta?: (module: SpecModule) => void;
  /** Shown on the empty-category state, root level only — the host's own
   *  primary action ("New module"). */
  emptyAction?: { label: string; onClick: () => void };
  /** Fires whenever the category list (re)loads, so a host that needs the
   *  tree itself (a "Category and tags…" dialog's picker) doesn't have to
   *  run a second fetch of the same table. */
  onCategoriesChange?: (categories: ModuleCategory[]) => void;
  /** Catalog tags (named, maybe unused) offered in the Tags rail at count 0. */
  catalogTags?: string[];
  /** Rendered in the table's view-options row, beside Columns / Group
   *  (the Preview toggle). */
  toolbarExtra?: React.ReactNode;
  /** `'page'` (default) = full Modules-page chrome. `'pick'` = a pick dialog:
   *  hides New category, the category ⋮ menus, the Tags pencil, the saved-view
   *  picker and the Aa slider. */
  chrome?: 'page' | 'pick';
}

export function ModuleBrowser({
  hostId,
  layout,
  compact,
  modules,
  userName,
  userPermissions,
  toast,
  storageKeyBase,
  columns: hostColumns,
  groupOptions,
  onRowClick,
  rowHref,
  selectable,
  selectionMode,
  selectedIds,
  onSelectionChange,
  bulkActions,
  searchPlaceholder,
  initialSort,
  onEditModuleMeta,
  emptyAction,
  onCategoriesChange,
  toolbarExtra,
  catalogTags,
  chrome = 'page',
}: ModuleBrowserProps) {
  const pick = chrome === 'pick';
  const cb = useModuleCategoryBrowser({
    hostId,
    modules,
    userName,
    userPermissions,
    toast,
    onCategoriesChange,
  });
  const {
    canCreateCategory,
    canEditCategory,
    canDeleteCategory,
    categories,
    categoriesLoaded,
    location,
    goTo,
    showAllCats,
    setShowAllCats,
    entries,
    tableModules,
    crumbs,
    currentLabel,
    tagsInScope,
    selectedTags,
    toggleTag,
    clearTags,
    tagFilterEmptiedResults,
    openNew,
    openRename,
    openMove,
    setDeleteTarget,
  } = cb;

  const visibleEntries =
    layout === 'stacked' && !showAllCats
      ? entries.slice(0, MODULE_BROWSER_STACKED_VISIBLE)
      : entries;
  const hiddenEntryCount = entries.length - visibleEntries.length;

  // ─── Columns: the host's own, with Category + Tags layered in after `name` ───
  const categoryCol: Column<SpecModule> = React.useMemo(
    () => ({
      key: 'category',
      label: 'Category',
      width: pick ? 120 : 180,
      getValue: (m) => categoryColumnValue(m, categories),
      filterType: 'multiselect',
      filterOptions: categoryFilterOptions(categories),
      defaultHidden: layout === 'stacked' && !!compact,
      render: (m) => {
        const label = categoryColumnValue(m, categories);
        const text = label || 'Uncategorized';
        if (!onEditModuleMeta) {
          return <span style={{ color: label ? 'var(--tx)' : 'var(--tm)' }}>{text}</span>;
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditModuleMeta(m);
            }}
            title="Edit category and tags"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              color: label ? 'var(--tx)' : 'var(--tm)',
              textDecoration: 'underline',
              textDecorationColor: 'var(--bd)',
              font: 'inherit',
            }}
          >
            {text}
          </button>
        );
      },
    }),
    [categories, layout, compact, onEditModuleMeta, pick]
  );

  const narrow = layout === 'stacked' && !!compact;
  const tagsCol: Column<SpecModule> = React.useMemo(
    () => ({
      key: 'tags',
      label: 'Tags',
      width: pick ? 150 : 220,
      getValue: (m) => tagsColumnValue(m),
      filterType: 'text',
      // Narrow dock (22rem): Category already hides itself here — Tags must
      // too, or it's the one column left wide enough to force a horizontal
      // scrollbar that clips the Insert button (Modules toolbox usability
      // fix). Still reachable from the Columns menu, same as Category.
      defaultHidden: narrow,
      render: (m) => {
        const tags = m.tags || [];
        if (!tags.length) return null;
        const shown = tags.slice(0, 3);
        const hidden = tags.length - shown.length;
        const chipStyle: React.CSSProperties = {
          fontSize: 'var(--font-size-xs)',
          padding: `0 ${rem(6)}`,
          borderRadius: 'var(--radius-full)',
          background: 'var(--sa)',
          color: 'var(--tm)',
          border: '1px solid var(--bd)',
          whiteSpace: 'nowrap',
          flex: '0 0 auto',
        };
        return (
          <span
            title={tags.join(', ')}
            style={{
              display: 'flex',
              gap: 'var(--space-1)',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {shown.map((t) => (
              <span key={t} style={chipStyle}>
                {t}
              </span>
            ))}
            {hidden > 0 && <span style={chipStyle}>+{hidden}</span>}
          </span>
        );
      },
    }),
    [narrow, pick]
  );

  const columns = React.useMemo(() => {
    const base = hostColumns.map((c) =>
      narrow && c.key !== 'name' ? { ...c, defaultHidden: true } : c
    );
    const nameIdx = base.findIndex((c) => c.key === 'name');
    const at = nameIdx >= 0 ? nameIdx + 1 : base.length;
    return [...base.slice(0, at), categoryCol, tagsCol, ...base.slice(at)];
  }, [hostColumns, narrow, categoryCol, tagsCol]);

  const categoryGroupOption: GroupOption<SpecModule> = React.useMemo(
    () => ({
      key: 'category',
      label: 'Category',
      getValue: (m) => categoryColumnValue(m, categories) || 'Uncategorized',
    }),
    [categories]
  );
  const mergedGroupOptions = React.useMemo(
    () => [categoryGroupOption, ...(groupOptions || [])],
    [categoryGroupOption, groupOptions]
  );

  // Only `stacked` (the panel) has a real narrow/wide split — the library's
  // `rail` layout keeps its ORIGINAL storage key so existing saved column
  // widths/filters on that table are never orphaned by this change.
  const storageKey =
    layout === 'stacked' ? `${storageKeyBase}.${narrow ? 'compact' : 'wide'}` : storageKeyBase;

  // `storageKeyBase` was bumped `.v1` → `.v2` for the panel (stale hidden-
  // columns snapshot) — seed v2 from v1 once so nothing else saved there
  // (order/widths/sort/group/filters/text size) is lost, and tell DataTable
  // the v1 key too, for its own firm-default read-only fallback.
  const legacyStorageKeyBase = layout === 'stacked' ? storageKeyBase.replace(/\.v2$/, '.v1') : null;
  const hasLegacyBase = !!legacyStorageKeyBase && legacyStorageKeyBase !== storageKeyBase;
  const legacyStorageKey = hasLegacyBase
    ? `${legacyStorageKeyBase}.${narrow ? 'compact' : 'wide'}`
    : undefined;
  const seededKeysRef = React.useRef<Set<string>>(new Set());
  if (hasLegacyBase && legacyStorageKey && !seededKeysRef.current.has(storageKey)) {
    seededKeysRef.current.add(storageKey);
    // `.compact` drops the hidden-columns list (see `seedV2FromV1`); `.wide`
    // (narrow === false) carries over everything.
    seedV2FromV1(legacyStorageKey, storageKey, narrow);
  }

  // ─── `rail` layout's category column width (fraction, floored, capped) ───
  const railOuterRef = React.useRef<HTMLDivElement>(null);
  const [railWidth, setRailWidth] = React.useState(220);
  React.useEffect(() => {
    if (layout !== 'rail') return;
    const el = railOuterRef.current;
    if (!el) return;
    const measure = () => setRailWidth(moduleBrowserRailWidthPx(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout]);

  // ─── Pieces shared by both layouts ───

  const breadcrumb = (
    <nav
      aria-label="Category breadcrumb"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: rem(4),
        flexWrap: 'wrap',
        fontSize: 'var(--font-size-sm)',
        minHeight: rem(22),
      }}
    >
      <button
        type="button"
        onClick={() => goTo({ kind: 'root' })}
        disabled={location.kind === 'root'}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: location.kind === 'root' ? 'default' : 'pointer',
          fontWeight: location.kind === 'root' ? 700 : 400,
          color: location.kind === 'root' ? 'var(--tx)' : 'var(--ac)',
        }}
      >
        All modules
      </button>
      {location.kind === 'uncategorized' && (
        <>
          <Icon icon={ChevronRight} size="xs" aria-hidden="true" style={{ color: 'var(--tm)' }} />
          <span style={{ fontWeight: 700, color: 'var(--tx)' }}>Uncategorized</span>
        </>
      )}
      {crumbs.map((c, i) => (
        <React.Fragment key={c.id}>
          <Icon icon={ChevronRight} size="xs" aria-hidden="true" style={{ color: 'var(--tm)' }} />
          {i === crumbs.length - 1 ? (
            <span style={{ fontWeight: 700, color: 'var(--tx)' }}>{c.name}</span>
          ) : (
            <button
              type="button"
              onClick={() => goTo({ kind: 'category', id: c.id })}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--ac)',
              }}
            >
              {c.name}
            </button>
          )}
        </React.Fragment>
      ))}
    </nav>
  );

  const newCategoryButton = !pick && canCreateCategory && location.kind !== 'uncategorized' && (
    <Button
      variant="outline"
      size="sm"
      onClick={() => openNew(location.kind === 'category' ? location.id : null)}
    >
      <Icon icon={Plus} size="sm" aria-hidden="true" /> New category
    </Button>
  );

  const noCategoriesHint = !pick &&
    location.kind === 'root' &&
    categoriesLoaded &&
    categories.length === 0 && (
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', margin: 0 }}>
        No categories yet.
        {canCreateCategory && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => openNew(null)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--ac)',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              New category
            </button>
          </>
        )}
      </p>
    );

  const entryCards = visibleEntries.map((entry) =>
    entry.kind === 'uncategorized' ? (
      <button
        key="__uncategorized__"
        type="button"
        onClick={() => goTo({ kind: 'uncategorized' })}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: `${rem(8)} var(--space-3)`,
          borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--bd)',
          background: 'var(--sf)',
          cursor: 'pointer',
          minHeight: rem(34),
          color: 'var(--tm)',
          textAlign: 'left',
        }}
      >
        <Icon icon={Folder} size="sm" aria-hidden="true" style={{ flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Uncategorized
        </span>
        <span style={{ fontSize: 'var(--font-size-sm)', flexShrink: 0 }}>{entry.count}</span>
      </button>
    ) : (
      <CategoryEntryCard
        key={entry.cat.id}
        cat={entry.cat}
        count={entry.count}
        canCreate={!pick && canCreateCategory}
        canEdit={!pick && canEditCategory}
        canDelete={!pick && canDeleteCategory}
        onOpen={() => goTo({ kind: 'category', id: entry.cat.id })}
        onNewSub={() => openNew(entry.cat.id)}
        onRename={() => openRename(entry.cat)}
        onMove={() => openMove(entry.cat)}
        onDelete={() => setDeleteTarget(entry.cat)}
      />
    )
  );

  const showAllToggle = layout === 'stacked' && hiddenEntryCount > 0 && (
    <button
      type="button"
      onClick={() => setShowAllCats(true)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: 'var(--ac)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      Show all ({entries.length})
    </button>
  );

  const tagsRail = (
    <ModuleTagsRail
      hostId={hostId}
      layout={layout}
      tags={tagsInScope}
      catalogTags={catalogTags}
      selected={selectedTags}
      onToggle={toggleTag}
      onClear={clearTags}
      userName={userName}
      userPermissions={userPermissions}
      toast={toast}
      readOnly={pick}
    />
  );

  const tableArea =
    tableModules.length === 0 ? (
      tagFilterEmptiedResults ? (
        <EmptyState
          icon={<Icon icon={Folder} em={2.1538} aria-hidden="true" />}
          title="No modules with these tags here"
          action={{ label: 'Clear', onClick: clearTags }}
        />
      ) : (
        <EmptyState
          icon={<Icon icon={Folder} em={2.1538} aria-hidden="true" />}
          title={`No modules in ${currentLabel} yet`}
          subtitle={
            location.kind === 'root'
              ? undefined
              : 'Open a module and set its category to put it here.'
          }
          action={location.kind === 'root' ? emptyAction : undefined}
        />
      )
    ) : (
      <DataTable<SpecModule>
        columns={columns}
        data={tableModules}
        keyField="id"
        onRowClick={onRowClick}
        rowHref={rowHref}
        groupOptions={mergedGroupOptions}
        searchPlaceholder={searchPlaceholder || 'Search modules, categories, tags…'}
        initialSort={initialSort || { key: 'name', direction: 'asc' }}
        storageKey={storageKey}
        legacyStorageKey={legacyStorageKey}
        selectable={selectable}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        permissions={userPermissions}
        bulkActions={bulkActions}
        compactToolbar={narrow}
        toolbarExtra={toolbarExtra}
        hideViewChrome={pick}
      />
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        gap: 'var(--space-2)',
      }}
    >
      {layout === 'rail' ? (
        <div
          ref={railOuterRef}
          style={{ display: 'flex', flex: 1, minHeight: 0, gap: 'var(--space-3)' }}
        >
          <div
            style={{
              width: railWidth,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              minHeight: 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {breadcrumb}
              {newCategoryButton}
              {noCategoriesHint}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {entryCards}
              </div>
              {tagsRail}
            </div>
          </div>
          <div
            style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            {tableArea}
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {breadcrumb}
            <div style={{ flex: 1 }} />
            {newCategoryButton}
          </div>
          {noCategoriesHint}
          {entries.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {entryCards}
              {showAllToggle}
            </div>
          )}
          {tagsRail}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {tableArea}
          </div>
        </>
      )}

      <ModuleCategoryDialogs state={cb} />
    </div>
  );
}

export default ModuleBrowser;
